/**
 * Care Queue Notes Section for Resident Detail Dialog
 *
 * Displays care queue notes with author, timestamp, and priority badges.
 * Matches Figma design: cate_queue_notes_section.png
 */

import { MessageSquare, AlertTriangle } from 'lucide-react';
import { CareQueueNotesResponse } from "../../../types/residents";
import { formatDateTime } from "../../../utils/dateFormat";

interface CareQueueNotesSectionProps {
  data: CareQueueNotesResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function CareQueueNotesSection({
  data,
  isLoading,
  error,
}: CareQueueNotesSectionProps) {
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          Loading care queue notes...
        </div>
      );
    }

    if (error) {
      return (
        <div className="py-4 text-center text-destructive">
          Error loading notes: {error.message}
        </div>
      );
    }

    if (!data || data.notes.length === 0) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          No care queue notes available
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {data.notes.map((note, index) => (
          <div
            key={note.id || index}
            className="p-4 border border-gray-200 rounded-lg bg-white"
          >
            {/* Timestamp + Priority Badge */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gray-600">
                {formatDateTime(note.timestamp)}
              </span>
              {note.high_priority && (
                <span className="inline-flex items-center gap-1 bg-[#B02828] text-white text-[11px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                  <AlertTriangle className="w-3 h-3" />
                  HIGH PRIORITY
                </span>
              )}
            </div>
            {/* Author */}
            <p className="text-xs text-gray-600 mb-2">{note.author}</p>
            {/* Content */}
            <p className="text-sm text-gray-800 leading-relaxed">{note.content}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mb-6">
      {/* Section Header */}
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="w-5 h-5 text-[#228189]" />
        <h2 className="text-base font-bold text-gray-900">
          Care Queue Notes
        </h2>
      </div>

      {renderContent()}
    </div>
  );
}
